import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Heart, MessageSquare, Microscope, UserPlus, Pin, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";

const KIND_ICONS = {
  appreciate: Heart,
  comment: MessageSquare,
  critique: Microscope,
  follow: UserPlus,
  pick: Pin,
};

const POLL_INTERVAL_MS = 45_000;

function timeAgo(iso, t) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return t("just_now");
    if (diff < 3600) return `${Math.floor(diff / 60)}${t("m")}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}${t("h")}`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}${t("d")}`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

function NotificationLine({ notif, onClick, t }) {
  const Icon = KIND_ICONS[notif.kind] || Bell;
  const body = t(`notif_${notif.kind}`).replace("{name}", notif.actor_name || t("someone"));
  return (
    <Link
      to={notif.target_url || "/"}
      onClick={onClick}
      className={`flex items-start gap-3 px-3 py-3 hover:bg-zinc-900 transition-colors border-l-2 ${
        notif.read ? "border-transparent" : "border-sienna bg-zinc-900/40"
      }`}
      data-testid={`notif-item-${notif.id}`}
    >
      <span
        className={`mt-0.5 w-7 h-7 inline-flex items-center justify-center border ${
          notif.read ? "border-zinc-800 text-zinc-500" : "border-sienna/50 text-sienna"
        }`}
      >
        <Icon size={13} />
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-zinc-200 font-light leading-snug">
          <span className={notif.read ? "text-zinc-300" : "text-zinc-100"}>{body}</span>
          {notif.target_title && (
            <span className="ml-1 text-zinc-400 italic">
              “{notif.target_title}”
            </span>
          )}
        </div>
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 mt-1">
          {timeAgo(notif.updated_at || notif.created_at, t)}
        </div>
      </div>
    </Link>
  );
}

export default function NotificationBell() {
  const { user } = useAuth();
  const { t } = useLang();
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const fetchCount = useCallback(async () => {
    if (!user || user === false) {
      setUnread(0);
      return;
    }
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(Number(data?.count || 0));
    } catch {
      /* silent — bell is non-critical */
    }
  }, [user]);

  useEffect(() => {
    fetchCount();
    if (pollRef.current) clearInterval(pollRef.current);
    if (user && user !== false) {
      pollRef.current = setInterval(fetchCount, POLL_INTERVAL_MS);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [user, fetchCount]);

  const loadFeed = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 25 } });
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setBusy(false);
    }
  }, []);

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) {
      loadFeed();
      // Optimistically clear the badge — server mark-read fires in background.
      if (unread > 0) {
        setUnread(0);
        api.post("/notifications/mark-read", {}).catch(() => {});
      }
    }
  };

  const markAllRead = async () => {
    setItems((prev) => (prev || []).map((n) => ({ ...n, read: true })));
    setUnread(0);
    try {
      await api.post("/notifications/mark-read", {});
    } catch { /* */ }
  };

  if (!user || user === false) return null;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          className="relative inline-flex items-center justify-center w-9 h-9 text-zinc-300 hover:text-zinc-50 transition-colors"
          aria-label={t("notifications")}
          data-testid="notification-bell-button"
        >
          <Bell size={18} strokeWidth={1.5} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-sienna text-white text-[9px] font-mono font-bold rounded-full inline-flex items-center justify-center"
              data-testid="notification-unread-badge"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0 bg-zinc-950 border border-zinc-800 text-zinc-100 max-h-[480px] overflow-hidden flex flex-col"
        data-testid="notification-menu"
      >
        <div className="px-4 py-3 border-b border-zinc-900 flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
            {t("notifications")}
          </div>
          {items && items.some((n) => !n.read) && (
            <button
              onClick={markAllRead}
              className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 hover:text-zinc-100 transition-colors"
              data-testid="notif-mark-all-read"
            >
              <CheckCheck size={11} />
              {t("notif_mark_all_read")}
            </button>
          )}
        </div>
        <div className="overflow-y-auto flex-1">
          {busy && items === null ? (
            <div className="px-4 py-10 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 text-center">
              {t("loading")}
            </div>
          ) : !items || items.length === 0 ? (
            <div className="px-4 py-12 text-center" data-testid="notif-empty">
              <div className="w-10 h-10 mx-auto border border-zinc-800 inline-flex items-center justify-center text-zinc-600 mb-3">
                <Bell size={16} strokeWidth={1.5} />
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                {t("notif_empty")}
              </div>
            </div>
          ) : (
            items.map((n) => (
              <NotificationLine key={n.id} notif={n} t={t} onClick={() => setOpen(false)} />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
